// TemplatesPage — sibling subpage to `DashboardsPage`, mounted under
// `/dashboards/templates`. Same layout (left sidebar + main pane), same
// sidebar action plumbing — but typed for templates and pointed at the
// templates RTK slice.
//
// New templates are seeded with `TEMPLATE_STARTER_CODE` +
// `TEMPLATE_STARTER_DEFAULTS` so the editor opens onto a renderable
// component out of the gate (and so the agent's `add_template`
// equivalent has a faithful UI counterpart).
//
// Selection lives on the URL path:
//
//   /dashboards/templates/<templatePath>
//   /dashboards/templates/f/<folderPath>
//
// The tab strip links at the bare templates home, so the last selection is
// also remembered in localStorage and reopened when the user comes back to it
// with nothing selected (`useExplorerLastVisited`).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { LayoutTemplate, Loader2 } from 'lucide-react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import {
  useCreateTemplateFolderMutation,
  useCreateTemplateMutation,
  useDeleteTemplateFolderByPathMutation,
  useDeleteTemplateFolderMutation,
  useDeleteTemplateMutation,
  useListTemplateFoldersQuery,
  useListTemplatesQuery,
  useMoveTemplateFolderMutation,
  useRenameTemplateFolderMutation,
  useRenameTemplateMutation,
  useUpdateTemplateContentMutation,
  type Template,
  type TemplateFolder
} from '@axilon-apps/domains-ontology-ui-state'

import { useChatAppContext } from '../../chat-app-context'
import {
  ActionErrorDialog,
  countPrefixDescendants,
  CreateItemDialog,
  DeleteItemDialog,
  expandAncestors,
  isPathPrefix,
  joinUnderParent,
  leafSegment,
  MoveItemDialog,
  parentPathOf,
  readMutationError,
  relocateUnderPrefix,
  RenameItemDialog,
  subtreeFolderPaths,
  type CreateItemKind,
  type DeleteItemKind,
  type MoveItemKind,
  type RenameItemKind
} from '../../components/explorer'
import {
  buildDashboardsAppContextEntries,
  DASHBOARDS_SUPERSEDED_PARAMS
} from './dashboards-app-context'
import type { DashboardsOutletContext } from './DashboardsHomePage'
import { TemplateEditor, TEMPLATE_STARTER_CODE, TEMPLATE_STARTER_DEFAULTS } from './editor'
import type { EditorMode } from './editor/editor-mode'
import { TEMPLATES_HOME, templateFolderHref, templateHref } from './explorer-paths'
import type { LastVisitedSelection } from './last-visited'
import TemplateTreeSidebar, {
  type TemplateSelection,
  type TemplateSidebarAction
} from './TemplateTreeSidebar'
import { useExplorerLastVisited } from './useExplorerLastVisited'
import { useExplorerSelection } from './useExplorerSelection'

interface CreateDialogState {
  kind: Extract<CreateItemKind, 'folder' | 'template'>
  parentPath: string
}

interface RenameDialogState {
  kind: Extract<RenameItemKind, 'folder' | 'template'>
  path: string
  name: string
  folder?: TemplateFolder
  template?: Template
}

interface DeleteDialogState {
  kind: Extract<DeleteItemKind, 'folder' | 'template'>
  path: string
  name: string
  folder?: TemplateFolder
  template?: Template
}

interface MoveDialogState {
  kind: Extract<MoveItemKind, 'folder' | 'template'>
  path: string
  name: string
  template?: Template
}

const TemplatesPage = () => {
  const navigate = useNavigate()
  const { tabsSlot } = useOutletContext<DashboardsOutletContext>()
  const [searchParams, setSearchParams] = useSearchParams()
  const routeSelection = useExplorerSelection('templates')

  const foldersQuery = useListTemplateFoldersQuery()
  const templatesQuery = useListTemplatesQuery()
  const folders = useMemo(() => foldersQuery.data ?? [], [foldersQuery.data])
  const templates = useMemo(() => templatesQuery.data ?? [], [templatesQuery.data])
  const isLoading = foldersQuery.isLoading || templatesQuery.isLoading

  const [createFolder] = useCreateTemplateFolderMutation()
  const [createTemplate] = useCreateTemplateMutation()
  const [updateContent] = useUpdateTemplateContentMutation()
  const [renameFolder] = useRenameTemplateFolderMutation()
  const [renameTemplate] = useRenameTemplateMutation()
  const [deleteFolder] = useDeleteTemplateFolderMutation()
  const [deleteFolderByPath] = useDeleteTemplateFolderByPathMutation()
  const [moveFolder] = useMoveTemplateFolderMutation()
  const [deleteTemplate] = useDeleteTemplateMutation()

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [filter, setFilter] = useState('')
  const [createDialog, setCreateDialog] = useState<CreateDialogState | null>(null)
  const [renameDialog, setRenameDialog] = useState<RenameDialogState | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState | null>(null)
  const [moveDialog, setMoveDialog] = useState<MoveDialogState | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    if (isLoading) return
    const legacyTemplateId = searchParams.get('template')
    const legacyFolder = searchParams.get('folder')
    if (legacyTemplateId === null && legacyFolder === null) return

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('template')
    nextParams.delete('folder')
    nextParams.delete('view')
    const search = nextParams.toString()
    const suffix = search === '' ? '' : `?${search}`

    if (legacyTemplateId) {
      const found = templates.find((t) => t.id === legacyTemplateId)
      if (found) {
        navigate(`${templateHref(found.path)}${suffix}`, { replace: true })
        return
      }
      setSearchParams(nextParams, { replace: true })
      return
    }

    if (legacyFolder) {
      navigate(`${templateFolderHref(legacyFolder)}${suffix}`, { replace: true })
      return
    }

    setSearchParams(nextParams, { replace: true })
  }, [isLoading, searchParams, templates, navigate, setSearchParams])

  const legacyParamsPending =
    searchParams.get('template') !== null ||
    searchParams.get('folder') !== null ||
    searchParams.get('view') !== null

  const lastVisitedHref = useCallback(
    (remembered: LastVisitedSelection): string | null => {
      if (remembered.kind === 'folder') {
        return folders.some((row) => row.path === remembered.path)
          ? templateFolderHref(remembered.path)
          : null
      }
      return templates.some((row) => row.path === remembered.path)
        ? templateHref(remembered.path)
        : null
    },
    [folders, templates]
  )

  useExplorerLastVisited({
    surface: 'templates',
    selection: routeSelection,
    ready: !isLoading,
    paused: legacyParamsPending,
    hrefFor: lastVisitedHref
  })

  const selectedTemplate = useMemo(() => {
    if (routeSelection.kind !== 'item') return undefined
    return templates.find((t) => t.path === routeSelection.path)
  }, [routeSelection, templates])

  const selection: TemplateSelection = useMemo(() => {
    if (routeSelection.kind === 'item' && selectedTemplate) {
      return { kind: 'template', id: selectedTemplate.id }
    }
    if (routeSelection.kind === 'folder') {
      return { kind: 'folder', path: routeSelection.path }
    }
    return { kind: 'none' }
  }, [routeSelection, selectedTemplate])

  const defaultParentForHeaderCreate = useMemo(() => {
    if (selection.kind === 'folder') return selection.path
    if (selection.kind === 'template') {
      const selected = templates.find((t) => t.id === selection.id)
      if (selected) return parentPathOf(selected.path)
    }
    return ''
  }, [selection, templates])

  const [editorMode, setEditorMode] = useState<EditorMode>('render')

  useChatAppContext(
    buildDashboardsAppContextEntries({
      view: 'templates',
      item: selectedTemplate,
      editorMode: selectedTemplate ? editorMode : undefined,
      folder: selection.kind === 'folder' ? selection.path : undefined
    }),
    DASHBOARDS_SUPERSEDED_PARAMS
  )

  const handleToggle = (path: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const handleSelectFolder = (path: string): void => {
    navigate(templateFolderHref(path), { replace: true })
  }

  const handleSelectTemplate = (id: string): void => {
    if (id === '') {
      navigate(TEMPLATES_HOME, { replace: true })
      return
    }
    const found = templates.find((t) => t.id === id)
    if (!found) return
    navigate(templateHref(found.path), { replace: true })
  }

  const goHomeIfUnder = (prefix: string) => {
    if (selection.kind === 'folder' && isPathPrefix(prefix, selection.path)) {
      navigate(TEMPLATES_HOME, { replace: true })
    }
    if (selectedTemplate && isPathPrefix(prefix, selectedTemplate.path)) {
      navigate(TEMPLATES_HOME, { replace: true })
    }
  }

  const afterFolderMoved = (fromPath: string, toPath: string) => {
    setExpanded((prev) => expandAncestors(prev, toPath))
    if (selection.kind === 'folder' && isPathPrefix(fromPath, selection.path)) {
      navigate(templateFolderHref(relocateUnderPrefix(fromPath, toPath, selection.path)), {
        replace: true
      })
    }
    if (selectedTemplate && isPathPrefix(fromPath, selectedTemplate.path)) {
      navigate(templateHref(relocateUnderPrefix(fromPath, toPath, selectedTemplate.path)), {
        replace: true
      })
    }
  }

  const moveFolderToParent = async (fromPath: string, toParentPath: string, name?: string) => {
    const toPath = joinUnderParent(toParentPath, leafSegment(fromPath))
    if (toPath === fromPath) return
    await moveFolder({ fromPath, toPath, ...(name !== undefined ? { name } : {}) }).unwrap()
    afterFolderMoved(fromPath, toPath)
  }

  const moveTemplateToParent = async (template: Template, toParentPath: string) => {
    const toPath = joinUnderParent(toParentPath, leafSegment(template.path))
    if (toPath === template.path) return
    const updated = await renameTemplate({ id: template.id, input: { path: toPath } }).unwrap()
    setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
    navigate(templateHref(updated.path), { replace: true })
  }

  const handleSidebarAction = (action: TemplateSidebarAction): void => {
    switch (action.kind) {
      case 'create-folder':
        setCreateDialog({ kind: 'folder', parentPath: action.parentPath })
        return
      case 'create-template':
        setCreateDialog({ kind: 'template', parentPath: action.parentPath })
        return
      case 'rename-folder':
        setRenameDialog({
          kind: 'folder',
          path: action.path,
          name: action.name,
          folder: action.folder
        })
        return
      case 'rename-template':
        setRenameDialog({
          kind: 'template',
          path: action.template.path,
          name: action.template.name,
          template: action.template
        })
        return
      case 'move-folder':
        setMoveDialog({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'move-template':
        setMoveDialog({
          kind: 'template',
          path: action.template.path,
          name: action.template.name,
          template: action.template
        })
        return
      case 'drop-folder':
        void moveFolderToParent(action.fromPath, action.toParentPath).catch((err) => {
          setActionError(readMutationError(err))
        })
        return
      case 'drop-template':
        void moveTemplateToParent(action.template, action.toParentPath).catch((err) => {
          setActionError(readMutationError(err))
        })
        return
      case 'delete-folder':
        setDeleteDialog({
          kind: 'folder',
          path: action.path,
          name: action.name,
          folder: action.folder
        })
        return
      case 'delete-template':
        setDeleteDialog({
          kind: 'template',
          path: action.template.path,
          name: action.template.name,
          template: action.template
        })
        return
    }
  }

  const handleCreate = async ({ name, path }: { name: string; path: string }): Promise<void> => {
    if (!createDialog) return
    if (createDialog.kind === 'folder') {
      const created = await createFolder({ name, path }).unwrap()
      setExpanded((prev) => expandAncestors(prev, created.path))
      handleSelectFolder(created.path)
    } else {
      const created = await createTemplate({ name, path }).unwrap()
      await updateContent({
        id: created.id,
        input: { code: TEMPLATE_STARTER_CODE, default_values: TEMPLATE_STARTER_DEFAULTS }
      }).unwrap()
      setExpanded((prev) => expandAncestors(prev, parentPathOf(created.path)))
      handleSelectTemplate(created.id)
    }
  }

  const handleRename = async ({ name, path }: { name: string; path?: string }): Promise<void> => {
    if (!renameDialog) return
    if (renameDialog.kind === 'folder') {
      const fromPath = renameDialog.path
      const toPath = path ?? fromPath
      if (renameDialog.folder?.id && toPath === fromPath) {
        await renameFolder({ id: renameDialog.folder.id, input: { name } }).unwrap()
        return
      }
      if (toPath !== fromPath) {
        await moveFolder({ fromPath, toPath, name }).unwrap()
        afterFolderMoved(fromPath, toPath)
      }
      return
    }
    if (renameDialog.kind === 'template' && renameDialog.template) {
      const input = path !== undefined ? { name, path } : { name }
      const updated = await renameTemplate({
        id: renameDialog.template.id,
        input
      }).unwrap()
      if (updated.path !== renameDialog.path) {
        setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
        navigate(templateHref(updated.path), { replace: true })
      }
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (!deleteDialog) return
    if (deleteDialog.kind === 'folder') {
      if (deleteDialog.folder?.id) {
        await deleteFolder(deleteDialog.folder.id).unwrap()
      } else {
        await deleteFolderByPath({ path: deleteDialog.path }).unwrap()
      }
      goHomeIfUnder(deleteDialog.path)
      return
    }
    if (deleteDialog.kind === 'template' && deleteDialog.template) {
      const template = deleteDialog.template
      await deleteTemplate(template.id).unwrap()
      if (selection.kind === 'template' && selection.id === template.id) {
        navigate(TEMPLATES_HOME, { replace: true })
      }
    }
  }

  const handleMove = async (destinationFolderPath: string) => {
    if (!moveDialog) return
    if (moveDialog.kind === 'folder') {
      await moveFolderToParent(moveDialog.path, destinationFolderPath)
      return
    }
    if (moveDialog.template) {
      await moveTemplateToParent(moveDialog.template, destinationFolderPath)
    }
  }

  const dialogOpen =
    createDialog !== null ||
    renameDialog !== null ||
    deleteDialog !== null ||
    moveDialog !== null ||
    actionError !== null

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (dialogOpen) return
      const target = event.target
      if (target instanceof HTMLElement) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return
        }
        if (target.closest('.monaco-editor')) return
      }
      if (selection.kind === 'folder') {
        const folder = folders.find((row) => row.path === selection.path)
        const name = folder?.name ?? leafSegment(selection.path)
        if (event.key === 'F2') {
          event.preventDefault()
          setRenameDialog({ kind: 'folder', path: selection.path, name, folder })
          return
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault()
          setDeleteDialog({ kind: 'folder', path: selection.path, name, folder })
        }
        return
      }
      if (selection.kind === 'template' && selectedTemplate) {
        if (event.key === 'F2') {
          event.preventDefault()
          setRenameDialog({
            kind: 'template',
            path: selectedTemplate.path,
            name: selectedTemplate.name,
            template: selectedTemplate
          })
          return
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault()
          setDeleteDialog({
            kind: 'template',
            path: selectedTemplate.path,
            name: selectedTemplate.name,
            template: selectedTemplate
          })
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [dialogOpen, folders, selectedTemplate, selection])

  const moveDisabledPaths = useMemo(() => {
    const blocked = new Set<string>()
    if (moveDialog?.kind === 'folder') {
      for (const path of subtreeFolderPaths(moveDialog.path, folders, templates)) {
        blocked.add(path)
      }
    }
    return blocked
  }, [moveDialog, folders, templates])

  return (
    /* [contain:size] keeps the explorer from inflating AppLayout's outlet
       wrapper (same Docs/Tags pattern): without it, min-height:auto grows
       with the editor and the collapse handle centers on the whole page. */
    <div className="flex h-full min-h-0 flex-col overflow-hidden [contain:size]">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <TemplateTreeSidebar
          folders={folders}
          templates={templates}
          selection={selection}
          onSelectFolder={handleSelectFolder}
          onSelectTemplate={handleSelectTemplate}
          expanded={expanded}
          onToggle={handleToggle}
          onAction={handleSidebarAction}
          filter={filter}
          onFilterChange={setFilter}
          isLoading={isLoading}
          topSlot={tabsSlot}
          createParentPath={defaultParentForHeaderCreate}
        />

        <div className="relative z-0 min-w-0 flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-ink-3">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading templates…
            </div>
          ) : selectedTemplate ? (
            <TemplateEditor
              template={selectedTemplate}
              mode={editorMode}
              onModeChange={setEditorMode}
              onRename={() =>
                setRenameDialog({
                  kind: 'template',
                  path: selectedTemplate.path,
                  name: selectedTemplate.name,
                  template: selectedTemplate
                })
              }
              onMove={() =>
                setMoveDialog({
                  kind: 'template',
                  path: selectedTemplate.path,
                  name: selectedTemplate.name,
                  template: selectedTemplate
                })
              }
              onDelete={() =>
                setDeleteDialog({
                  kind: 'template',
                  path: selectedTemplate.path,
                  name: selectedTemplate.name,
                  template: selectedTemplate
                })
              }
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
              <LayoutTemplate className="size-8 text-ink-3" aria-hidden />
              <p className="m-0 text-sm text-ink-3">
                {folders.length === 0 && templates.length === 0
                  ? 'No templates or folders yet. Create one to get started.'
                  : routeSelection.kind === 'item'
                    ? 'Template not found.'
                    : 'Select a template from the sidebar.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <CreateItemDialog
        open={createDialog !== null}
        kind={createDialog?.kind ?? 'folder'}
        folderNoun="template folder"
        defaultParentPath={createDialog?.parentPath ?? defaultParentForHeaderCreate}
        folders={folders}
        dashboards={templates}
        onClose={() => setCreateDialog(null)}
        onSubmit={handleCreate}
      />

      <RenameItemDialog
        open={renameDialog !== null}
        kind={renameDialog?.kind ?? 'folder'}
        folderNoun="template folder"
        currentName={renameDialog?.name ?? ''}
        path={renameDialog?.path ?? ''}
        rewriteLeafPath
        onClose={() => setRenameDialog(null)}
        onSubmit={handleRename}
      />

      <MoveItemDialog
        open={moveDialog !== null}
        kind={moveDialog?.kind ?? 'template'}
        name={moveDialog?.name ?? ''}
        currentPath={moveDialog?.path ?? ''}
        folders={folders}
        dashboards={templates}
        disabledPaths={moveDisabledPaths}
        onClose={() => setMoveDialog(null)}
        onSubmit={handleMove}
      />

      <DeleteItemDialog
        open={deleteDialog !== null}
        kind={deleteDialog?.kind ?? 'folder'}
        folderNoun="template folder"
        name={deleteDialog?.name ?? ''}
        path={deleteDialog?.path ?? ''}
        descendantCount={
          deleteDialog?.kind === 'folder'
            ? countPrefixDescendants(deleteDialog.path, folders, templates)
            : 0
        }
        recursive={deleteDialog?.kind === 'folder'}
        onClose={() => setDeleteDialog(null)}
        onConfirm={handleDelete}
      />

      <ActionErrorDialog
        open={actionError !== null}
        message={actionError ?? ''}
        onClose={() => setActionError(null)}
      />
    </div>
  )
}

export default TemplatesPage
